import { useEffect, useRef, useState } from "react";
import "./PageOutline.css";

type Entry = { id: string; title: string; parent?: string };

export default function PageOutline() {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [active, setActive] = useState("");
  const [open, setOpen] = useState(false);
  const [gutter, setGutter] = useState(0);
  const [light, setLight] = useState(false);
  const currentRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    setLight(main.matches(".expedition-page, .ms-page, .nier-page"));
    const labels = new Map(Array.from(main.querySelectorAll<HTMLAnchorElement>('nav a[href^="#"], .toc a[href^="#"]')).map(link => [link.hash.slice(1), link.textContent?.trim() || ""]));
    const sections = Array.from(main.querySelectorAll<HTMLElement>("section[id]"));
    const items = sections.flatMap(section => {
      const heading = Array.from(section.querySelectorAll("h2, h3")).find(node => node.closest("section[id]") === section);
      const title = labels.get(section.id) || (heading as HTMLElement | undefined)?.innerText?.replace(/\s+/g, " ").trim();
      return title ? [{ id: section.id, title, parent: section.parentElement?.closest("section[id]")?.id }] : [];
    });
    setEntries(items);
    let frame = 0;
    const update = () => {
      frame = 0;
      // Read live positions: media loading and code disclosures can change chapter heights.
      let current = items[0]?.id || "";
      for (const item of items) {
        if (document.getElementById(item.id)!.getBoundingClientRect().top <= 120) current = item.id;
      }
      if (window.scrollY > 0 && window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) current = items[items.length - 1]?.id || current;
      setActive(current);
      const shells = Array.from(main.querySelectorAll(".container, .ms-shell, .nier-shell"));
      if (main.matches(".container")) shells.push(main);
      const edge = Math.max(...shells.map(node => node.getBoundingClientRect().right));
      setGutter(Number.isFinite(edge) ? Math.max(0, Math.min(192, window.innerWidth - edge - 32)) : 0);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const observer = new ResizeObserver(schedule);
    observer.observe(main);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    update();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, []);

  useEffect(() => {
    const link = currentRef.current;
    const list = link?.closest(".page-outline-list");
    if (link && list) {
      const offset = link.getBoundingClientRect().top - list.getBoundingClientRect().top;
      if (offset < 0 || offset + link.offsetHeight > list.clientHeight) list.scrollTop += offset - list.clientHeight / 2;
    }
  }, [active, open]);

  if (entries.length < 2) return null;
  const rail = gutter >= 136;
  const current = entries.find(item => item.id === active);
  return <aside className={`page-outline ${light ? "page-outline-light" : ""} ${rail ? "page-outline-rail" : "page-outline-compact"}`} style={rail ? { width: gutter } : undefined} aria-label="페이지 위치">
    <button className="page-outline-toggle" type="button" aria-expanded={open} aria-controls="page-outline-links" onClick={() => setOpen(value => !value)}>
      <span>현재 위치 · 목차</span><strong>{current?.title}</strong><span aria-hidden="true">{open ? "닫기 −" : "열기 +"}</span>
    </button>
    <nav id="page-outline-links" aria-label="페이지 목차" hidden={!rail && !open} onKeyDown={event => { if (event.key === "Escape") { setOpen(false); event.currentTarget.parentElement?.querySelector("button")?.focus(); } }}>
      <div className="page-outline-heading">이 페이지에서</div>
      <ol className="page-outline-list">{entries.map(item => <li key={item.id} className={item.parent ? "page-outline-child" : ""}>
        <a ref={item.id === active ? currentRef : undefined} href={`#${item.id}`} aria-current={item.id === active ? "location" : undefined} data-in-section={entries.find(entry => entry.id === active)?.parent === item.id || undefined} onClick={event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          const target = document.getElementById(item.id);
          if (!target) return;
          window.history.pushState(null, "", `#${item.id}`);
          target.scrollIntoView({ behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" });
          target.setAttribute("tabindex", "-1");
          target.focus({ preventScroll: true });
          setActive(item.id);
          setOpen(false);
        }}>{item.title}</a>
      </li>)}</ol>
      <a className="page-outline-top" href="#top" onClick={() => setOpen(false)}>맨 위로 ↑</a>
    </nav>
  </aside>;
}
